import { useState, useEffect, useRef } from 'react'
import { useLocation, useNavigate, Navigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Header } from '@/components/layout/Header'
import { useProfile } from '@/hooks/useProfile'
import { usePageTitle } from '@/hooks/useDocumentHead'
import { AlertCircle, Loader2, UserPlus, Upload, X, ArrowRight, Check } from 'lucide-react'
import { imageFileToSquarePngDataUrl } from '@/utils/image'

export function ProfileSetup() {
  usePageTitle('Complete Your Profile')
  const { createDraft, createProfile, needsCreate, isLoading } = useProfile()
  const navigate = useNavigate()
  const location = useLocation()
  
  // If user already has a profile, redirect them away from signup
  if (!isLoading && !needsCreate) {
    const state = location.state as { from?: { pathname?: string } } | null
    const returnTo = state?.from?.pathname || '/groups'
    return <Navigate to={returnTo} replace />
  }
  const [displayName, setDisplayName] = useState('')
  const [picturePreview, setPicturePreview] = useState<string | null>(null)
  const [hasNewPicture, setHasNewPicture] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const returnTo = (() => {
    const state = location.state as { from?: { pathname?: string; search?: string; hash?: string } } | null
    const from = state?.from
    if (!from?.pathname) return null
    return `${from.pathname}${from.search || ''}${from.hash || ''}`
  })()

  useEffect(() => {
    setDisplayName(createDraft.displayName || createDraft.name || '')
    setPicturePreview(createDraft.picture || null)
    setHasNewPicture(false)
    setError(null)
  }, [createDraft])

  const MAX_FILE_SIZE = 2.5 * 1024 * 1024 // 2.5MB

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    if (file.size > MAX_FILE_SIZE) {
      setError(`File size must be less than 2.5MB. Current size: ${(file.size / 1024 / 1024).toFixed(2)}MB`)
      return
    }

    if (!file.type.startsWith('image/')) {
      setError('Please select an image file')
      return
    }

    setError(null)

    try {
      const dataUrl = await imageFileToSquarePngDataUrl(file, 256)
      setPicturePreview(dataUrl)
      setHasNewPicture(true)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to process image')
    } finally {
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    }
  }

  const handleRemovePicture = () => {
    setHasNewPicture(false)
    setPicturePreview(createDraft.picture || null)
  }

  const handleComplete = async () => {
    setIsSaving(true)
    setError(null)

    try {
      await createProfile({
        displayName: displayName.trim() || undefined,
        picture: picturePreview || undefined,
      })

      // Redirect back to where user came from (e.g. accepting an invite)
      if (returnTo) {
        navigate(returnTo, { replace: true })
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create profile')
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5 relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-24 -right-24 w-96 h-96 bg-primary/10 rounded-full blur-3xl" />
        <div className="absolute -bottom-24 -left-24 w-96 h-96 bg-primary/5 rounded-full blur-3xl" />
      </div>

      <div className="relative z-10 min-h-screen flex flex-col">
        <Header />

        <main className="flex-1 container mx-auto px-4 py-12 flex flex-col items-center justify-center">
          {/* Progress indicator */}
          <div className="mb-8 flex items-center gap-3">
            <div className="flex flex-col items-center gap-1">
              <div className="h-8 w-8 rounded-full bg-primary flex items-center justify-center">
                <Check className="h-4 w-4 text-primary-foreground" />
              </div>
              <span className="text-xs text-primary font-medium">Sign In</span>
            </div>
            <div className="w-8 h-0.5 bg-primary mb-5" />
            <div className="flex flex-col items-center gap-1">
              <div className="h-8 w-8 rounded-full bg-primary flex items-center justify-center">
                <span className="text-sm font-bold text-primary-foreground">2</span>
              </div>
              <span className="text-xs text-primary font-medium">Profile</span>
            </div>
            <div className="w-8 h-0.5 bg-border mb-5" />
            <div className="flex flex-col items-center gap-1">
              <div className="h-8 w-8 rounded-full bg-muted flex items-center justify-center">
                <span className="text-sm text-muted-foreground">3</span>
              </div>
              <span className="text-xs text-muted-foreground">Explore</span>
            </div>
          </div>

          {/* Welcome message - smaller text but good spacing */}
          <div className="text-center mb-8 max-w-md">
            <h1 className="text-2xl font-bold mb-2">
              Complete Your Profile
            </h1>
            <p className="text-muted-foreground">
              Set up how other players will see you
            </p>
          </div>

          {/* Profile setup card */}
          <Card className="w-full max-w-md border-border/50 dark:shadow-lg">
            <CardContent className="pt-8 pb-8 space-y-6">
              {/* Profile Picture */}
              <div className="flex flex-col items-center gap-3">
                <Label className="text-sm font-medium">Profile Picture</Label>
                <div className="relative">
                  {picturePreview ? (
                    <div className="relative">
                      <img
                        src={picturePreview}
                        alt="Profile preview"
                        className="h-24 w-24 rounded-full object-cover border-2 border-primary/20"
                      />
                      {hasNewPicture && (
                        <button
                          onClick={handleRemovePicture}
                          className="absolute -top-1 -right-1 h-6 w-6 rounded-full bg-destructive text-destructive-foreground flex items-center justify-center hover:bg-destructive/90"
                          type="button"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      )}
                    </div>
                  ) : (
                    <div className="h-24 w-24 rounded-full bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center border-2 border-primary/20">
                      <UserPlus className="h-10 w-10 text-primary/60" />
                    </div>
                  )}
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileSelect}
                  className="hidden"
                  id="profile-picture-upload"
                />
                <Label htmlFor="profile-picture-upload" className="cursor-pointer">
                  <Button variant="outline" type="button" size="sm" asChild>
                    <span>
                      <Upload className="h-4 w-4 mr-2" />
                      {picturePreview ? 'Change Photo' : 'Upload Photo'}
                    </span>
                  </Button>
                </Label>
              </div>

              {/* Display Name */}
              <div className="space-y-2">
                <Label htmlFor="display-name" className="text-sm font-medium">
                  Display Name
                </Label>
                <Input
                  id="display-name"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  placeholder="Enter your display name"
                  maxLength={50}
                  autoFocus
                />
                <p className="text-xs text-muted-foreground">
                  This is how other players will see you on leagues
                </p>
              </div>

              {/* Email badge */}
              <div className="flex items-center justify-center gap-2 py-2 px-4 rounded-md bg-muted/50 text-sm">
                <span className="text-muted-foreground">Signed in as</span>
                <span className="font-medium truncate max-w-[200px]">{createDraft.email || 'your account'}</span>
              </div>

              {/* Error Message */}
              {error && (
                <div className="rounded-md bg-destructive/10 border border-destructive/20 p-3 flex items-center gap-2 text-sm text-destructive">
                  <AlertCircle className="h-4 w-4 flex-shrink-0" />
                  {error}
                </div>
              )}

              {/* Complete Setup Button */}
              <Button
                onClick={handleComplete}
                disabled={isSaving}
                className="w-full h-11"
                size="lg"
              >
                {isSaving ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Setting up...
                  </>
                ) : (
                  <>
                    Complete Setup
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </>
                )}
              </Button>

              <p className="text-xs text-center text-muted-foreground">
                You can update this later in settings
              </p>
            </CardContent>
          </Card>
        </main>
      </div>
    </div>
  )
}

